import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';
import {
  ACTIVITY_TYPE_ICONS,
  ACTIVITY_TYPE_LABELS,
  LEAD_STAGES,
  type ActivityRow,
  type LeadRow,
} from '../types';

interface LeadModalProps {
  lead: LeadRow;
  password: string;
  onClose: () => void;
  onChanged: () => void;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function LeadModal({ lead, password, onClose, onChanged }: LeadModalProps) {
  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [loadingActs, setLoadingActs] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [name, setName] = useState(lead.name);
  const [company, setCompany] = useState(lead.company ?? '');
  const [phone, setPhone] = useState(lead.phone ?? '');
  const [email, setEmail] = useState(lead.email ?? '');
  const [source, setSource] = useState(lead.source ?? '');
  const [value, setValue] = useState(lead.value != null ? String(lead.value) : '');
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [salesperson, setSalesperson] = useState(lead.salesperson ?? '');
  const [nextFollowUp, setNextFollowUp] = useState(
    lead.next_follow_up ? lead.next_follow_up.slice(0, 16) : '',
  );

  const [newType, setNewType] = useState('nota');
  const [newContent, setNewContent] = useState('');
  const [newFollowUp, setNewFollowUp] = useState('');
  const [addingAct, setAddingAct] = useState(false);

  const loadActivities = async () => {
    setLoadingActs(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-admin', {
        body: { action: 'list_activities', password, lead_id: lead.id },
      });
      if (invokeErr) {
        const msgErr = await extractErrorMessage(invokeErr);
        throw new Error(msgErr);
      }
      const parsed = data as { activities?: ActivityRow[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setActivities(parsed?.activities ?? []);
    } catch {
      // silencioso
    } finally {
      setLoadingActs(false);
    }
  };

  useEffect(() => {
    loadActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);

  const saveLead = async () => {
    if (!name.trim()) {
      setError('Nome do contato é obrigatório.');
      return;
    }
    setSavingLead(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-admin', {
        body: {
          action: 'update_lead',
          password,
          lead_id: lead.id,
          name,
          company,
          phone,
          email,
          source,
          value: value ? Number(value) : 0,
          notes,
          salesperson,
          next_follow_up: nextFollowUp || null,
        },
      });
      if (invokeErr) {
        const msgErr = await extractErrorMessage(invokeErr);
        throw new Error(msgErr);
      }
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar lead.');
    } finally {
      setSavingLead(false);
    }
  };

  const moveStage = async (stage: string) => {
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-admin', {
        body: { action: 'update_lead_stage', password, lead_id: lead.id, stage },
      });
      if (invokeErr) {
        const msgErr = await extractErrorMessage(invokeErr);
        throw new Error(msgErr);
      }
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao mover lead.');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-admin', {
        body: { action: 'delete_lead', password, lead_id: lead.id },
      });
      if (invokeErr) {
        const msgErr = await extractErrorMessage(invokeErr);
        throw new Error(msgErr);
      }
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setConfirmDelete(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao excluir lead.');
    } finally {
      setDeleting(false);
    }
  };

  const addActivity = async () => {
    if (!newContent.trim()) return;
    setAddingAct(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-admin', {
        body: {
          action: 'create_activity',
          password,
          lead_id: lead.id,
          type: newType,
          content: newContent.trim(),
          follow_up_at: newFollowUp || null,
        },
      });
      if (invokeErr) {
        const msgErr = await extractErrorMessage(invokeErr);
        throw new Error(msgErr);
      }
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setNewContent('');
      setNewFollowUp('');
      await loadActivities();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao adicionar atividade.');
    } finally {
      setAddingAct(false);
    }
  };

  const toggleActivity = async (activityId: number, done: boolean) => {
    try {
      await supabase.functions.invoke('crm-admin', {
        body: { action: 'toggle_activity', password, activity_id: activityId, done },
      });
      setActivities((prev) =>
        prev.map((a) => (a.id === activityId ? { ...a, done } : a)),
      );
    } catch {
      // silencioso
    }
  };

  const deleteActivity = async (activityId: number) => {
    try {
      await supabase.functions.invoke('crm-admin', {
        body: { action: 'delete_activity', password, activity_id: activityId },
      });
      setActivities((prev) => prev.filter((a) => a.id !== activityId));
    } catch {
      // silencioso
    }
  };

  const pendingActs = activities.filter((a) => !a.done);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full max-w-3xl bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-hop-border px-6 py-4 flex items-center justify-between gap-4 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
              <i className="ri-user-star-line text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{lead.name}</h3>
              {lead.company && <p className="text-xs text-gray-500">{lead.company}</p>}
            </div>
          </div>
          <button
            onClick={() => setConfirmDelete(true)}
            title="Excluir lead"
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer shrink-0"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
          )}

          {/* Etapa */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Etapa do funil</h4>
            <div className="flex flex-wrap gap-2">
              {LEAD_STAGES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => moveStage(s.key)}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-3 py-1.5 whitespace-nowrap cursor-pointer transition-colors duration-150 ${
                    lead.stage === s.key ? s.tone : 'bg-white text-gray-500 border-gray-200 hover:border-[#E8610A]'
                  }`}
                >
                  {lead.stage === s.key && <i className="ri-check-line"></i>}
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dados do lead */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Nome *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Empresa</label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Telefone</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Origem</label>
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="Ex: Site, WhatsApp, Indicação..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Valor estimado (R$)</label>
              <input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0,00"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Próximo follow-up</label>
              <input
                type="datetime-local"
                value={nextFollowUp}
                onChange={(e) => setNextFollowUp(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Responsável</label>
              <input
                type="text"
                value={salesperson}
                onChange={(e) => setSalesperson(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Anotações</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all resize-none"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveLead}
              disabled={savingLead}
              className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {savingLead ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Salvando...
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  Salvar
                </>
              )}
            </button>
          </div>

          {/* Atividades / follow-up */}
          <div className="border-t border-hop-border pt-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h4 className="font-bold text-gray-900">Follow-up e anotações</h4>
              {pendingActs.length > 0 && (
                <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                  {pendingActs.length} pendente{pendingActs.length === 1 ? '' : 's'}
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:border-[#E8610A] cursor-pointer"
              >
                {Object.entries(ACTIVITY_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <input
                type="text"
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="Ex: Ligar para confirmar interesse..."
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
              />
              <input
                type="datetime-local"
                value={newFollowUp}
                onChange={(e) => setNewFollowUp(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:border-[#E8610A]"
              />
              <button
                onClick={addActivity}
                disabled={addingAct || !newContent.trim()}
                className="inline-flex items-center justify-center gap-1.5 bg-[#E8610A] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {addingAct ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
                Adicionar
              </button>
            </div>

            {loadingActs ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : activities.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                Nenhuma atividade registrada ainda.
              </p>
            ) : (
              <div className="space-y-2">
                {activities.map((a) => (
                  <div
                    key={a.id}
                    className={`flex items-start gap-3 border rounded-xl p-3 ${
                      a.done ? 'border-hop-border bg-hop-bg-alt' : 'border-hop-border'
                    }`}
                  >
                    <button
                      onClick={() => toggleActivity(a.id, !a.done)}
                      className={`w-6 h-6 shrink-0 flex items-center justify-center border rounded-full cursor-pointer ${
                        a.done
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-gray-300 text-transparent hover:border-green-500'
                      }`}
                    >
                      <i className="ri-check-line text-xs"></i>
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">
                          <i className={ACTIVITY_TYPE_ICONS[a.type] ?? 'ri-sticky-note-line'}></i>
                          {ACTIVITY_TYPE_LABELS[a.type] ?? a.type}
                        </span>
                        {a.follow_up_at && (
                          <span className="text-xs text-amber-700 font-semibold whitespace-nowrap">
                            <i className="ri-alarm-line"></i> {formatDate(a.follow_up_at)}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {new Date(a.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                        </span>
                      </div>
                      <p className={`text-sm text-gray-700 mt-1 ${a.done ? 'line-through text-gray-400' : ''}`}>
                        {a.content}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteActivity(a.id)}
                      className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 cursor-pointer shrink-0"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !deleting && setConfirmDelete(false)}
          ></div>
          <div className="relative w-full max-w-sm bg-white rounded-2xl p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 text-red-600 rounded-xl mx-auto mb-4">
              <i className="ri-delete-bin-line text-xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center">Excluir lead?</h3>
            <p className="text-sm text-gray-500 text-center mt-2">
              Você vai excluir o lead de{' '}
              <strong className="text-gray-700">{lead.name}</strong>.
              {lead.value != null && (
                <span> Valor: {formatBRL(Number(lead.value))}.</span>
              )}
            </p>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold text-gray-600 px-4 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-red-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-red-700 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Excluindo...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Excluir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}